import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { IAuthenticationService } from '../interfaces/authentication.abstract.service';
import { Observable } from 'rxjs';
import { PershingHttpUtil } from './pershing-http.util';

/**
 * Takes outbound request and adds X-JWT-Assertion header
 * with jwt token as the value
 */
@Injectable()
export class JwtHeaderInterceptor implements HttpInterceptor {
    private authenticationService: IAuthenticationService;

    constructor(private injector: Injector) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (PershingHttpUtil.isAuthRequired(request.url, this.injector.get('AppSettings'))) {
            // Cyclic dependency error: https://github.com/angular/angular/issues/18224
            this.authenticationService = this.injector.get(IAuthenticationService);

            // Check if DISABLE-JWT header is found
            if (request.headers.has('DISABLE-JWT')) {
                // Delete only that header. HttpHeaders is immutable
                const headers = request.headers.delete('DISABLE-JWT');
                // Clone the request since HttpRequest objects are immutable
                request = request.clone({ headers });
            } else {
                // If DISABLE-JWT header is not found, check if user is logged in. If not, skip
                if (this.authenticationService.isLoggedIn() && this.authenticationService.getToken()) {
                    // Clone the request since HttpRequest objects are immutable
                    request = request.clone({
                        headers: request.headers.set('X-JWT-Assertion', this.authenticationService.getToken())
                    });
                }
            }
        }
        return next.handle(request);
    }
}

/**
 * Takes outbound requests and adds netx.esf.AuthenticationService header
 * with cookie string as the value
 */
@Injectable()
export class EsfHeaderInterceptor implements HttpInterceptor {
    private authenticationService: IAuthenticationService;

    constructor(private injector: Injector) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (PershingHttpUtil.isAuthRequired(request.url, this.injector.get('AppSettings'))) {
            // Cyclic dependency error: https://github.com/angular/angular/issues/18224
            this.authenticationService = this.injector.get(IAuthenticationService);

            // Check if DISABLE-ESF header is found
            if (request.headers.has('DISABLE-ESF')) {
                // Delete only that header. HttpHeaders is immutable
                const headers = request.headers.delete('DISABLE-ESF');
                // Clone the request since HttpRequest objects are immutable
                request = request.clone({ headers });
            } else {
                // we don't need ESF in the request header for portal since everything is based on esf cookie in the portal
                // If DISABLE-ESF header is not found, check if user is logged in. If not, skip
                if (this.authenticationService.isLoggedIn() && this.authenticationService.getCookie()) {
                    // Clone the request since HttpRequest objects are immutable
                    request = request.clone({
                        headers: request.headers.set('netx.esf.AuthenticationService', this.authenticationService.getCookie())
                    });
                }
            }
        }
        return next.handle(request);
    }
}


@Injectable()
export class SensitiveRequestHeadersInterceptor implements HttpInterceptor {
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (request.method === 'GET' && request.headers && request.headers.has('secure-url')) {
            // Delete only that header. HttpHeaders is immutable
            let headers = request.headers.delete('secure-url');
            // Clone the request since HttpRequest objects are immutable
            request = request.clone({ headers });

            // Get access to URL string.
            const urlString = request.url;
            // Generate URL string regex. Taken from https://stackoverflow.com/a/26766402
            // tslint:disable-next-line:max-line-length
            const urlParseRegExp = /^(([^:\/?#]+):)?(\/\/([^\/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?/;

            // Given the example of full url:
            //      https://netx360wealth.dev.bnymellon.net/dev-netx360-balances-svc/accounts/511000000?arg=0-a&arg1=1-b&arg3-c#hash
            // Index:
            // 0 - full match.
            // 1 - https:
            // 2 - https
            // 3 - //netx360wealth.dev.bnymellon.net
            // 4 - netx360wealth.dev.bnymellon.net
            // 5 - /dev-netx360-balances-svc/accounts/511000000
            // 6 - ?arg=0-a&arg1=1-b&arg3-c
            // 7 - arg=0-a&arg1=1-b&arg3-c
            // 8 - #hash
            // 9 - hash
            const result = urlString.match(urlParseRegExp);

            // Get the pathname (i.e. dev-netx360-balances-svc-app/balances/account/511000000)
            let pathName = result[5];
            // Generate RegExp object to find sensitive resources.
            const resourceRegExp = new RegExp(`/(${this.getJoinedResources(sensitiveResources)})/([a-zA-Z0-9]+)`, 'g');
            // Replace all the sensitive resource values with a placeholder. First argument is regexp, second argument
            // is a function. In this case, it is an anonymous function that has replacement logic
            // pathName.replace(resourceRegExp, resourceReplacer);
            pathName = pathName.replace(resourceRegExp, (match: string, resource: string, resourceValue: number) => {
                // Create request headers to be added to the request
                headers = request.headers.append(`x-${sensitiveResources[resource]}`, resourceValue.toString());
                return `/${resource}/${sensitiveResources[resource]}`;
            });

            // TODO: Currently, there is no use case to handle sensitive query parameters.
            // You can target the 6th or 7th capture group to find sensitive parameters

            // Replace the 5th capture group in the RegExpMatchArray object with newly modified/secured pathname
            const securedUrl = urlString.replace(urlParseRegExp, `$1$3${pathName}$6$8`);
            // Clone the request since HttpRequest objects are immutable. Reassign new url and headers.
            request = request.clone({ url: securedUrl, headers });
        }
        return next.handle(request);
    }

    private getJoinedResources(resources: any) {
        return Object.keys(resources).join('|');
    }
}

export const sensitiveResources = {
    accounts: 'accountNumber'
};

// TODO: Currently, there is no use case to handle sensitive query parameters
// export const sensitiveParameters = [
// ];

export function resourceReplacer(match: string, resource: string, resourceValue: number): string {
    // The match is /resource/resourceValue group that was captured
    // i.e. /accounts/511000000 --> /accounts/accountNumber
    return `/${resource}/${sensitiveResources[resource]}`;
}
